import { zodResolver } from '@hookform/resolvers/zod';
import { api } from '@packages/backend/convex/_generated/api';
import {
  getUserErrorMessage,
  type RestaurantCvx,
  restaurantSchema,
  restaurantStrictSchema,
} from '@packages/shared';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { RestaurantList } from '@/components/RestaurantList/RestaurantList';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ActionButton } from '@/components/ui+/ActionButton';
import { FormWrapper } from '@/components/ui+/FormWrapper';
import {
  ResponsiveDialog,
  ResponsiveDialogBody,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
} from '@/components/ui+/ResponsiveDialog';
import { handleSubmitErrors } from '@/utils/handleSubmitErrors';

export const Route = createFileRoute('/r/')({
  beforeLoad: ({ context }) => {
    if (context.convexUser.userType === 'restaurantUser') {
      const restaurantIds = context.convexUser.restaurantUser.restaurantIds;
      if (restaurantIds.length === 0) {
        throw new Error(`Restaurant 'user' has no restaurants assigned!`);
      }
      if (restaurantIds.length === 1 && restaurantIds[0]) {
        throw redirect({
          params: { restaurantId: restaurantIds[0] },
          to: '/r/$restaurantId',
        });
      }
      // Otherwise, continue and show a list of restaurants.
      // This applies to 'user' with multiple restaurants and 'admin' users.
    }
  },
  component: Component,
});

function Component() {
  return (
    <div className="p-6 min-h-[calc(100vh-(--spacing(16)))] bg-muted/60 dark:bg-muted/20">
      <RestaurantList className="lg:w-1/2" showAddRestaurant={true} />
    </div>
  );
}

const AddRestaurant = () => {
  const convex = useConvex();
  const [open, setOpen] = useState(false);

  const formValuesSchema = z.object({
    restaurant: restaurantStrictSchema,
  });
  type FormValues = z.output<typeof formValuesSchema>;
  const defaultValues = {
    restaurant: restaurantSchema.decode({ name: '' }),
  };

  const form = useForm({
    defaultValues,
    resolver: zodResolver(formValuesSchema),
  });

  useEffect(() => {
    if (open) {
      form.reset(defaultValues);
    }
  }, [open]);

  const handleValidSubmit = async (formValues: FormValues) => {
    try {
      await convex.mutation(api.restaurants.create, formValues);
      setOpen(false);
      toast.success('Saved successfully');
    } catch (error) {
      const description = getUserErrorMessage(error);
      toast.error('Error saving', { description, duration: 10000 });
    }
  };

  return (
    <ResponsiveDialog onOpenChange={setOpen} open={open}>
      <ResponsiveDialogTrigger asChild>
        <ActionButton>Add Restaurant</ActionButton>
      </ResponsiveDialogTrigger>
      <ResponsiveDialogContent>
        <FormWrapper {...form}>
          <form
            className="space-y-8"
            onSubmit={form.handleSubmit(handleValidSubmit, handleSubmitErrors)}
          >
            <ResponsiveDialogHeader>
              <ResponsiveDialogTitle>Add New</ResponsiveDialogTitle>
              <ResponsiveDialogDescription>Create a new restaurant.</ResponsiveDialogDescription>
            </ResponsiveDialogHeader>
            <ResponsiveDialogBody className="grid gap-4">
              {/* Restaurant name */}
              <FormField
                control={form.control}
                name="restaurant.name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Restaurant Name</FormLabel>
                    <FormControl>
                      <Input {...field} required />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Restaurant uuid */}
              <FormField
                control={form.control}
                name="restaurant.uuid"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>uuid/legacy restaurantId</FormLabel>
                    <FormControl>
                      <Input {...field} required />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </ResponsiveDialogBody>
            <ResponsiveDialogFooter>
              <ActionButton
                disabled={form.formState.isSubmitting || !form.formState.isDirty}
                loading={form.formState.isSubmitting}
                type="submit"
              >
                Create
              </ActionButton>
            </ResponsiveDialogFooter>
          </form>
        </FormWrapper>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
};

type DeleteRestaurantProps = {
  children?: React.ReactNode;
  restaurant: RestaurantCvx;
};
const DeleteRestaurant = ({ children, restaurant }: DeleteRestaurantProps) => {
  const convex = useConvex();
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteRestaurant = async (restaurantId: string) => {
    try {
      setIsDeleting(true);
      await convex.mutation(api.restaurants.destroy, { restaurantId });
      setIsDeleting(false);
      setOpen(false);
      toast.success('Deleted successfully');
    } catch (error) {
      const description = getUserErrorMessage(error);
      toast.error('Unable to delete!', { description });
    }
  };

  return (
    <AlertDialog onOpenChange={setOpen} open={open}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{`Delete '${restaurant.name}' restaurant?`}</AlertDialogTitle>
          <AlertDialogDescription>
            Linked menus, orders, end of day reports will also be deleted.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <ActionButton
            loading={isDeleting}
            onClick={() => handleDeleteRestaurant(restaurant._id)}
            variant={'destructive'}
          >
            Permanently delete
          </ActionButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
